import React, { useState } from "react";
import {
  FaRegCalendarAlt,
  FaLock,
  FaLockOpen,
  FaCheckCircle,
  FaArchive,
  FaDoorOpen,
  FaBan,
} from "react-icons/fa";
import HomeShell from "../components/HomeShell";
import SeasonStatusBar, { calcolaFase } from "../components/SeasonStatusBar";
import { useAuth } from "../context/AuthContext";
import { api } from "../api/api";
import { messaggioDelServer } from "../utils/enrolDati";
// L'anno come arriva dalla RISPOSTA delle rotte d'azione, col metro che ha sede
// unica in utils/stagione.js (`annoDiLista`), qui col nome di sempre.
//
// ⚠️ Serve, e non è pignoleria. La risposta riporta la cassettina com'è scritta su
// Firestore, dove un anno può stare in cifre ("2026"): AuthContext lo normalizza a
// intero, ma qui la risposta non passa di là. Senza questo passaggio
// `calcolaFase` confronterebbe "2026" con 2026 usando `===`, non combacerebbe
// mai, e la pagina dichiarerebbe una fase incoerente subito dopo un'azione RIUSCITA.
import { annoDiLista as annoDaRisposta } from "../utils/stagione";
// I pulsanti sono quelli di sistema (doc 07 §5.1 oro, §5.3 fantasma): il foglio
// che li definisce si importa come fanno le altre pagine che li usano
// (TeamAssignment.jsx, UserContextManager.jsx). Nessuno stile nuovo.
import "../components/AdminTools.css";
import "./SeasonManagement.css";

/**
 * Season management (SUPERADMIN only) — YS-10.
 *
 * Legge la stagione del club dalla sede condivisa (AuthContext, YS-5): operatingSeason,
 * enrollmentSeason, seasonStatus, enrollmentsOpen. Da YS-10b la pagina COMANDA le leve del
 * calendario, chiamando le rotte già sul server (gate SUPERADMIN):
 *
 *   /season/open-enrollment    apre l'annata successiva      — solo in stagione unica
 *   /season/toggle-enrollment  riapre le iscrizioni          — in ogni fase, se chiuse
 *   /season/withdraw           ritira l'annata aperta        — solo in sovrapposizione
 *   /season/close-season       fa salire l'anno operativo    — solo in sovrapposizione
 *
 * ⚠️ «Enrollments closed» è per ora uno STATO, non un freno: nessuna nascita lo consulta
 * ancora, e chiudere non impedisce a nessuno di iscriversi. Il blocco vero è YS-10c.
 * Questa pagina comanda le leve; non applica il divieto — e va scritto, perché chi la
 * guarda potrebbe crederlo già in vigore.
 *
 * ⚠️ «Close season» (YS-13) ha ora la sua rotta e il suo pulsante, ed è la QUARTA leva: fa
 * salire l'anno operativo, e null'altro. Non archivia, non azzera, non fa copie di
 * sicurezza — la card lo dice, perché fino a YS-13 prometteva tre cose che non esistono.
 *
 * Regole di identità grafica: doc 07 (palette, pulsanti §5, telaio §7, card §8). Testi a
 * schermo in inglese, "enrollment" (CLAUDE.md §3.7).
 */

export default function SeasonManagement() {
  // ⚠️ I TRE VALORI SI PRENDONO CON UN ALTRO NOME, di proposito. La sede condivisa
  // resta la verità dell'app, ma subito dopo un'azione riuscita questa pagina ne sa
  // una più fresca (la risposta della rotta), e per un istante le due divergono. I
  // nomi che il disegno usa — `enrollmentSeason` e compagni, qui sotto — sono quindi
  // i valori EFFETTIVI, non quelli del contesto: così tutto il JSX che c'era prima
  // resta identico e continua a dire il vero.
  const {
    operatingSeason: operatingDalContesto,
    enrollmentSeason: enrollmentDalContesto,
    seasonStatus,
    enrollmentsOpen: enrollmentsOpenDalContesto,
    // La rilettura della sola stagione (YS-10b, AuthContext): la si chiama dopo
    // ogni azione riuscita perché la stagione nuova arrivi a TUTTA l'app.
    refreshSeason,
  } = useAuth();

  // QUALE AZIONE È IN VOLO — il nome, non un booleano, perché il pulsante che l'ha
  // avviata deve poterlo dire ("Opening…") mentre gli altri si limitano a spegnersi.
  // ⚠️ UNA PER VOLTA: due comandi sovrapposti sulla stessa cassettina lascerebbero a
  // schermo il risultato del più LENTO, non dell'ultimo dato.
  // null | "open" | "toggle" | "withdraw" | "close"
  const [azioneInCorso, setAzioneInCorso] = useState(null);

  // Il rifiuto del server, per esteso. È qui che arriva il testo del «ritira»
  // negato — «Cannot withdraw: N members already enrolled for AAAA.» — e quel
  // conteggio è l'unica cosa che spiega il no: si mostra com'è.
  const [erroreAzione, setErroreAzione] = useState(null);

  // LA CASSETTINA APPENA SCRITTA, in attesa che la sede condivisa la rilegga.
  //
  // Le tre rotte rispondono con la cassettina aggiornata (stessa forma di
  // `GET /season-config`): mostrarla subito evita che la pagina resti, per il tempo
  // di un secondo giro di rete, sui valori di PRIMA — cioè che dica il falso proprio
  // nell'istante in cui l'utente ha appena agito. Si azzera appena `refreshSeason`
  // è tornata: da lì in poi la verità è quella condivisa, e questa copia
  // invecchierebbe in silenzio.
  const [appenaScritta, setAppenaScritta] = useState(null);

  // I VALORI EFFETTIVI. La copia locale vince finché c'è, il contesto governa dopo.
  const enrollmentSeason = appenaScritta ? appenaScritta.enrollmentSeason : enrollmentDalContesto;
  const operatingSeason = appenaScritta ? appenaScritta.operatingSeason : operatingDalContesto;
  const enrollmentsOpen = appenaScritta ? appenaScritta.enrollmentsOpen : enrollmentsOpenDalContesto;

  const errore = seasonStatus === "error";

  const fase = calcolaFase(enrollmentSeason, operatingSeason);

  // L'annata che «apri» aprirebbe. In stagione unica i due anni coincidono, quindi
  // è `operatingSeason + 1` detto dalla parte dell'iscrizione — la stessa aritmetica
  // che fa il server, e che il pulsante deve poter NOMINARE prima di eseguirla.
  const prossimaAnnata = enrollmentSeason == null ? null : enrollmentSeason + 1;

  // IL GESTO, uno solo per tutte e quattro le leve.
  //
  // Le quattro rotte hanno la STESSA forma — POST senza corpo, gate SUPERADMIN, risposta
  // uguale a `GET /season-config`, rifiuto in `{ error }` — quindi hanno un solo
  // esecutore: cambia il percorso, non il modo. Le differenze fra i gesti vivono
  // tutte sul server, che è dove devono stare.
  const esegui = async (nome, percorso) => {
    if (azioneInCorso) return;

    setAzioneInCorso(nome);
    setErroreAzione(null);

    try {
      // ⚠️ Corpo `{}` e non assente: `api.post` fa comunque `JSON.stringify(body)`
      // (api.js), e con `undefined` partirebbe una POST che DICHIARA
      // `Content-Type: application/json` e non porta corpo. Le rotte dal corpo
      // non leggono nulla — l'attore lo firma il token, mai la richiesta — ma la
      // richiesta deve essere ciò che dice di essere.
      const cassettina = await api.post(percorso, {});

      // Prima i valori nuovi a schermo…
      setAppenaScritta({
        enrollmentSeason: annoDaRisposta(cassettina?.enrollmentSeason),
        operatingSeason: annoDaRisposta(cassettina?.operatingSeason),
        // Stesso metro tollerante di server e contesto: chiuse solo su un `false`
        // esplicito.
        enrollmentsOpen: cassettina?.enrollmentsOpen === false ? false : true,
      });

      // …poi la sede condivisa, perché la stagione nuova non resti confinata a
      // questa pagina. Non è un lusso: gli anni li legge anche il cancello delle
      // tessere, e due parti dell'app su annate diverse sono un guaio silenzioso.
      // ⚠️ Guardia sul `typeof`, e non è paranoia: se `refreshSeason` non ci fosse, il
      // TypeError finirebbe nel `catch` qui sotto e la pagina direbbe «non sono
      // riuscito» dopo una scrittura RIUSCITA — cioè la bugia peggiore che possa
      // dire. È il difetto che `refreshUserFromBackend` ha già pagato una volta
      // (AuthContext, P9), e non si ripete. Senza la rilettura la pagina resta
      // comunque vera: la risposta ce l'ha già.
      const riletta = typeof refreshSeason === "function" ? await refreshSeason() : null;

      // Riuscita la rilettura, la copia locale ha finito il suo compito. Se invece è
      // fallita la si tiene: fra i valori APPENA SCRITTI e quelli di prima, i primi
      // sono più veri.
      if (riletta) setAppenaScritta(null);
    } catch (err) {
      // ⚠️ SI DISTINGUE IL RIFIUTO DAL GUASTO, come in `enrolShared`. Se il server ha
      // risposto, `err.status` c'è (lo attacca api.js) e il motivo sta nel corpo — ed
      // è lì che vive il no del «ritira», col conteggio dei tesserati. Se risposta non
      // ce n'è stata, non si può dare la colpa allo stato del club: può essere caduta
      // la linea, e in quel caso non è stato scritto nulla comunque.
      const dalServer = messaggioDelServer(err);

      setErroreAzione(
        dalServer ||
          (err && err.status
            ? "The club server refused the change. Nothing was saved."
            : "The club server could not be reached. Nothing was saved.")
      );
    } finally {
      setAzioneInCorso(null);
    }
  };

  // «RITIRA» — l'unico gesto che toglie qualcosa, e l'unico con doppia conferma.
  //
  // ⚠️ STESSO MODELLO DEL DELETE DI UN MEMBRO (MemberProfileModal.jsx,
  // scelta dell'utente in sede d'incarico): una domanda secca, poi la parola da
  // digitare per esteso. Non è una modale — nell'app la conferma scritta di un gesto
  // distruttivo si fa così, e la si copia com'è invece di inventarne una forma nuova.
  // Il confronto è SECCO e maiuscolo come là: "withdraw" minuscolo non passa.
  const ritira = () => {
    if (azioneInCorso) return;

    if (
      !window.confirm(
        `This will withdraw enrollment for ${enrollmentSeason ?? "the next season"} ` +
          "and close enrollments. Continue?"
      )
    ) {
      return;
    }

    const doppia = window.prompt('Type "WITHDRAW" to confirm:');

    if (doppia !== "WITHDRAW") {
      window.alert("Cancelled");
      return;
    }

    esegui("withdraw", "/season/withdraw");
  };

  // «CHIUDI LA STAGIONE» — l'anno operativo raggiunge quello d'iscrizione (YS-13).
  //
  // ⚠️ STESSA DOPPIA CONFERMA DEL RITIRO, che è quella del DELETE di un membro: si copia
  // la forma che c'è, non se ne inventa una nuova. Una differenza sola, e voluta: la
  // parola da digitare porta L'ANNO — `CLOSE 2026` — perché questo gesto non si può
  // disfare da nessuna schermata, e la conferma deve dire QUALE stagione si chiude, non
  // solo che se ne chiude una. Confronto secco e maiuscolo come là.
  const chiudi = () => {
    if (azioneInCorso) return;

    // La parola di conferma non può dipendere da un'invariante che vive altrove: senza
    // questa riga, un `operatingSeason` nullo la farebbe diventare "CLOSE null". Il caso
    // oggi non si dà — il pulsante esiste solo in `overlap`, e lì `calcolaFase` ha già
    // provato che i due anni sono interi — ma l'invariante sta in un altro file.
    if (operatingSeason == null) return;

    const parola = `CLOSE ${operatingSeason}`;

    if (
      !window.confirm(
        `This will make ${enrollmentSeason ?? "the next season"} the operating season. ` +
          `Season ${operatingSeason} will no longer be the active one. Continue?`
      )
    ) {
      return;
    }

    const doppia = window.prompt(`Type "${parola}" to confirm:`);

    if (doppia !== parola) {
      window.alert("Cancelled");
      return;
    }

    esegui("close", "/season/close-season");
  };

  return (
    <HomeShell backTo="/home/superadmin" width="admin">
      <section className="qpsc-season">
        {/* Intestazione */}
        <header className="qpsc-season__head">
          <div className="qpsc-season__title">
            <FaRegCalendarAlt className="qpsc-season__title-icon" aria-hidden="true" />
            <h1 className="qpsc-page-title">Season management</h1>
          </div>
          <div className="qpsc-season__role">
            <FaLock aria-hidden="true" />
            <span>Superadmin</span>
          </div>
        </header>

        {errore && (
          <p className="qpsc-season__error">
            The club season could not be loaded. Reload the page; if it keeps failing, the
            server may be unreachable.
          </p>
        )}

        {/* LA BARRA DI STATO DELLA STAGIONE — componente condiviso (doc 07 §8.2).
            I due anni, la fascia di fase e la riga delle iscrizioni non vivono più
            qui: erano tre blocchi fratelli disegnati da questo foglio, e la prima
            finestra che ne avesse avuto bisogno avrebbe dovuto ricopiarli.
            ⚠️ `override={appenaScritta}` — e vale SOLO per questa pagina, che la
            stagione la SCRIVE. Subito dopo un comando riuscito qui si conosce la
            cassettina appena scritta prima che la sede condivisa la rilegga, e se
            la rilettura fallisce (`refreshSeason` torna null su qualunque guasto)
            resta l'unica a conoscerla: senza questa prop la barra direbbe gli anni
            di PRIMA mentre i pulsanti qui sotto dicono i NUOVI. Ovunque altrove la
            barra si monta nuda e legge il contesto.
            ⚠️ LA NOTA È DELLA PAGINA, non della barra, e non è un dettaglio: chi
            chiude le iscrizioni deve sapere che il freno È attivo — da YS-10c la
            nascita legge questo valore e le nuove iscrizioni vengono rifiutate con
            «contact the club admin». I soci già iscritti non sono toccati. */}
        <SeasonStatusBar
          override={appenaScritta}
          note="Closing enrollments stops new sign-ups. Members already enrolled are not affected."
        />

        {/* ACTIONS — le quattro leve del calendario (YS-10b; la quarta è YS-13).
            ⚠️ STA QUI, sopra «Procedures», e non sotto: le procedure sono DESCRIZIONI —
            raccontano che cosa comporta un passaggio di stagione, non lo eseguono — e
            dei pulsanti messi sotto di esse si crederebbe che eseguano la procedura
            accanto a cui stanno. Sopra invece stanno attaccati ai valori e allo stato
            che cambiano davvero. Vale anche ora che «Close season» ha la sua rotta: il
            comando vive qui, la card resta il testo che lo spiega.
            ⚠️ Nessun pulsante fuori da "ready": prima di aver letto la cassettina non
            si sa nemmeno in che fase si è, e un comando dato alla cieca scriverebbe
            sull'anno sbagliato. Le leve che toccano GLI ANNI restano perciò legate alla
            loro fase — «Open» solo in stagione unica, «Withdraw» e «Close season» solo
            in sovrapposizione — e in "inconsistent" la coppia di anni si ripara dalla
            PUT, non da qui.
            ⚠️ UNA SOLA ECCEZIONE, ed è la ragione di questa fetta (SM-reopen): «Reopen»
            NON tocca gli anni — inverte un campo solo — e deve poter comparire ovunque
            le iscrizioni risultino chiuse, "inconsistent" compresa. Senza di ciò resta
            il vicolo cieco trovato in beta: il ritiro riporta a stagione unica LASCIANDO
            le iscrizioni chiuse, e da lì non c'era più alcun pulsante per riaprirle — si
            usciva solo dalla porta di servizio PUT /season-config. Per questo la
            condizione ammette anche il caso «chiuse», qualunque sia la fase. */}
        {seasonStatus === "ready" &&
          (fase === "single" || fase === "overlap" || enrollmentsOpen === false) && (
          <div className="qpsc-season__actions">
            <div className="qpsc-season__actions-label">Actions</div>

            {/* «RIAPRI LE ISCRIZIONI» — il solo gesto di rubinetto rimasto a schermo
                (SM-reopen). Non è più un interruttore: il club le iscrizioni non le
                chiude mai da questa pagina, quindi il verso «Close enrollments» è stato
                tolto, e con esso il ternario che sceglieva icona e testo.
                ⚠️ NON STA IN NESSUN RAMO DI FASE, di proposito: il rubinetto è un campo
                indipendente dagli anni, e il suo unico metro è `enrollmentsOpen === false`
                — lo stesso, parola per parola, di server, contesto e barra di stato.
                La rotta è quella di sempre e INVERTE: partendo da «chiuse», apre. */}
            {enrollmentsOpen === false && (
              <button
                type="button"
                className="qpsc-tool-btn"
                onClick={() => esegui("toggle", "/season/toggle-enrollment")}
                disabled={azioneInCorso !== null}
              >
                <FaLockOpen aria-hidden="true" />
                <span>
                  {azioneInCorso === "toggle" ? "Saving…" : "Reopen enrollments"}
                </span>
              </button>
            )}

            {/* STAGIONE UNICA — l'unico gesto possibile è aprire l'annata dopo. */}
            {fase === "single" && (
              <button
                type="button"
                className="qpsc-tool-btn"
                onClick={() => esegui("open", "/season/open-enrollment")}
                disabled={azioneInCorso !== null || prossimaAnnata == null}
              >
                <FaDoorOpen aria-hidden="true" />
                <span>
                  {azioneInCorso === "open"
                    ? "Opening…"
                    : `Open enrollment for ${prossimaAnnata ?? "the next season"}`}
                </span>
              </button>
            )}

            {/* SOVRAPPOSIZIONE — il ritiro e la chiusura della stagione. Il rubinetto
                non sta più qui: da SM-reopen vive sopra, fuori dai rami di fase, e nel
                solo verso «Reopen». */}
            {fase === "overlap" && (
              <>
                {/* §5.3 FANTASMA, e non l'oro: il ritiro non è l'azione che ci si
                    aspetta da questa pagina e non deve competere con «Reopen enrollments».
                    (La variante d'accento §5.1 sarebbe il posto giusto per un gesto
                    irreversibile, ma vive in MemberProfileModal.css e non è condivisa:
                    copiarne lo stile qui ne farebbe una terza sede. Vedi referto.)
                    La guardia vera comunque non è il colore: è la doppia conferma. */}
                <button
                  type="button"
                  className="qpsc-ghost-btn"
                  onClick={ritira}
                  disabled={azioneInCorso !== null}
                >
                  <FaBan aria-hidden="true" />
                  <span>
                    {azioneInCorso === "withdraw"
                      ? "Withdrawing…"
                      : `Withdraw ${enrollmentSeason ?? "enrollment"}`}
                  </span>
                </button>

                {/* «CHIUDI LA STAGIONE» (YS-13) — il verso opposto del ritiro, e per
                    questo gli sta accanto: da qui si esce dalla sovrapposizione o
                    tornando indietro (sopra) o andando avanti (qui).
                    Fantasma come il ritiro: la guardia non è il colore, è la doppia
                    conferma — e questa ne chiede una che porta l'anno.
                    L'icona è quella della card omonima, così pulsante e descrizione si
                    riconoscono come la stessa cosa. */}
                <button
                  type="button"
                  className="qpsc-ghost-btn"
                  onClick={chiudi}
                  disabled={azioneInCorso !== null}
                >
                  <FaArchive aria-hidden="true" />
                  <span>
                    {azioneInCorso === "close"
                      ? "Closing…"
                      : `Close season ${operatingSeason ?? "the current season"}`}
                  </span>
                </button>
              </>
            )}

            {/* Il no del server, per esteso: è qui che compare il conteggio dei
                tesserati che impedisce il ritiro. `role="alert"` perché è la risposta
                a un gesto appena fatto, e va letta anche da chi non guarda lo schermo. */}
            {erroreAzione && (
              <p className="qpsc-season__action-error" role="alert">
                {erroreAzione}
              </p>
            )}
          </div>
        )}

        {/* Procedures */}
        <div className="qpsc-season__procedures">
          <div className="qpsc-season__procedures-label">Procedures</div>

          {/* Open enrollment — informativa */}
          {/* ⚠️ D-ADMIN1 (RD-159): ICONA E TESTO SEGUONO LA PASTIGLIA. Erano fissi
              — spunta verde e «Accepting renewals…» — e a rubinetto chiuso
              contraddicevano il «Closed» accanto. Leggono lo stesso valore, con lo
              stesso metro (chiuse solo su un `false` esplicito). A chiuse: il
              lucchetto, nel grigio neutro delle altre card (niente `--ok`). */}
          <article className="qpsc-season__card">
            {enrollmentsOpen === false ? (
              <FaLock className="qpsc-season__card-icon" aria-hidden="true" />
            ) : (
              <FaCheckCircle
                className="qpsc-season__card-icon qpsc-season__card-icon--ok"
                aria-hidden="true"
              />
            )}
            <div className="qpsc-season__card-body">
              <div className="qpsc-season__card-head">
                <h2 className="qpsc-season__card-title">
                  Open enrollment for {enrollmentSeason ?? "the new season"}
                </h2>
                {/* ⚠️ LA PASTIGLIA DICE LO STATO VERO (SM-reopen). Fin qui mostrava
                    «Done» FISSA: diceva «fatto» anche a rubinetto chiuso, cioè proprio
                    quando la procedura NON era in corso. Ora legge lo stesso valore dei
                    pulsanti, col metro tollerante di sempre — chiuse solo su un `false`
                    esplicito. */}
                <span
                  className={
                    enrollmentsOpen === false
                      ? "qpsc-season__status qpsc-season__status--off"
                      : "qpsc-season__status qpsc-season__status--done"
                  }
                >
                  {enrollmentsOpen === false ? "Closed" : "Open"}
                </span>
              </div>
              <p className="qpsc-season__card-text">
                {enrollmentsOpen === false
                  ? `Renewals and new sign-ups for ${enrollmentSeason ?? "the new season"} are closed.`
                  : `Accepting renewals and new sign-ups for ${enrollmentSeason ?? "the new season"}.`}{" "}
                Nothing is deleted.
              </p>
            </div>
          </article>

          {/* Close season — la DESCRIZIONE del gesto, SOLO in sovrapposizione.
              In stagione unica la procedura non ha senso: non c'è nessuna annata
              dove andare. Vale l'ordine deciso a diario (S6, decisione 1): prima
              si apre il nuovo, poi si chiude il vecchio — quindi "chiudi" esiste
              solo dopo che l'apertura ha creato la sovrapposizione.
              ⚠️ I TESTI SONO STATI RISCRITTI (YS-13), e va detto perché. Fino a
              qui la card elencava quattro passi di cui TRE FALSI — copia di
              sicurezza, archiviazione dell'annata, azzeramento dei soci: nessuna
              delle tre esiste, né qui né altrove nell'app — e prometteva un
              rapporto e una copia che facessero da guardia. Il gesto è uno solo:
              l'anno operativo sale. La card ora dice quello, e dichiara la copia
              di sicurezza come un compito di chi la usa, non dell'app. */}
          {fase === "overlap" && (
            <article className="qpsc-season__card qpsc-season__card--flag">
              <FaArchive className="qpsc-season__card-icon" aria-hidden="true" />
              <div className="qpsc-season__card-body">
                <div className="qpsc-season__card-head">
                  <h2 className="qpsc-season__card-title">
                    Close season {operatingSeason ?? ""}
                  </h2>
                  <span className="qpsc-season__status qpsc-season__status--pending">
                    Needs confirmation
                  </span>
                </div>
                <ol className="qpsc-season__steps">
                  <li>Back up your data yourself — the app has no backup.</li>
                  <li>
                    Season {enrollmentSeason ?? "the next season"} becomes the operating
                    season.
                  </li>
                  <li>
                    Teams, player cards and photos follow{" "}
                    {enrollmentSeason ?? "the next season"}. Nothing is deleted or archived.
                  </li>
                </ol>
                <p className="qpsc-season__card-text">
                  This cannot be undone from this page.
                </p>
              </div>
            </article>
          )}
        </div>
        {/* ⚠️ QUI STAVA il piè «Everything before step 4 is reversible.» (YS-13, tolto).
            Era falso due volte: nessuna rotta riporta indietro l'anno operativo — il
            ritiro muove l'ALTRO campo — e la riga era resa anche in stagione unica, dove
            nessun "passo 4" è a schermo. Ciò che diceva di vero — non si torna indietro —
            vive ora nel piè della card, accanto al gesto che descrive. */}
      </section>
    </HomeShell>
  );
}
